import React, { useEffect, useState } from 'react';
import {
  Badge, Button, Modal, Spinner,
} from 'react-bootstrap';
import EmailInboxPanel from '../../../Components/EmailInboxPanel';
import AiStatusDot from './AiStatusDot';
import AiDryRunService, {
  DRY_RUN_PATH,
  dryRunErrorText,
  isNotDeployed,
  traceOutcomeDescriptor,
} from '../../../Services/AiDryRunService';
import { authorityDescriptor } from '../../../Services/AiInstructionsService';
import { formatDateTime } from '../../../Utils/timezone';

/**
 * TEST IT BEFORE PUBLISHING — the saved draft, one real past email, the trace.
 *
 * ── THE TRACE IS THE PRODUCT ──
 * The loop this whole screen exists for is: adjust a rule, run it against a
 * handful of emails whose right answer you already know, read what the agent
 * did, adjust again, publish. What makes that loop work is seeing WHICH TOOL
 * THE AGENT CHOSE and what came back — that is what tells you whether the
 * sentence you just wrote did anything. A single verdict would not, and a form
 * that invoked one tool with hand-typed parameters would be testing the tool,
 * which is not the thing being edited.
 *
 * ── IT RUNS A SAVED VERSION, NOT THE BOX ──
 * The same gate as publish, and for a stronger reason. A dry run against the
 * textarea's current contents would let somebody test text that was never
 * stored and then publish a version that was never tested. Running the stored
 * version makes "what you tested is what you publish" structural: the two
 * calls name the same version number, and that number is what every decision
 * records.
 *
 * ── NOTHING IS SENT, QUEUED, PUBLISHED OR RECORDED ──
 * Said in the panel, and read back off the response rather than assumed: the
 * server answers `executed: false`, and a run that came back claiming
 * otherwise is a contract breach worth showing rather than hiding.
 *
 * ── THE EMAIL PICKER IS THE INBOX BROWSER WE ALREADY HAVE ──
 * `EmailInboxPanel` already lists the SES inbound bucket with search and
 * paging and is used by three other screens. It is reused rather than
 * reinvented; its `onSelect` gained a second argument (the email row) so a
 * caller that needs the KEY can have it, which is additive and leaves the
 * three existing callers reading exactly as they did.
 *
 * ── THE ENDPOINT IS NOT DEPLOYED YET ──
 * See AiDryRunService, which is the contract. Until it lands this panel's
 * Run button answers 404, and that case is told apart from a refusal and
 * named — an admin must not go looking for a fault in their instruction when
 * the truth is that this half is still being built.
 */

/** A `Mixed` field, shown as it was stored. The shape is the agent's own. */
const asJson = (value) => JSON.stringify(value, null, 2);

/** "—" rather than a blank, so an absent fact reads as absent. */
const or = (value) => (value === 0 || value ? value : '—');

/** An authority as a word, falling back to its own value. */
const authorityLabel = (value) => authorityDescriptor(value)?.label || value || '—';

/**
 * One step of the trace: which tool, under what authority, and what happened.
 *
 * The tool's LABEL comes from the catalogue and its KEY is shown beside it,
 * because the key is the name the model actually called and the label is the
 * only part a reader can scan. A tool the catalogue does not know still
 * renders — under its key — rather than vanishing from the trace, which is
 * the one thing a record of what the agent did must never do.
 */
function TraceStep({ step, index, tools }) {
  const [open, setOpen] = useState(false);
  const tool = tools.find((t) => t.key === step.tool);
  const outcome = traceOutcomeDescriptor(step.outcome);
  const hasDetail = step.arguments !== undefined
    || step.result !== undefined
    || !!step.error;

  return (
    <li className="ai-trace-step">
      <div className="ai-trace-head">
        <span className="ai-trace-index">{index + 1}</span>
        <span className="ai-trace-tool">
          <span className="ai-trace-tool-name">{tool?.label || step.tool || 'Unnamed call'}</span>
          {step.tool && <code className="ai-tool-key">{step.tool}</code>}
          {tool?.writes && (
            <Badge bg="warning" text="dark" className="ai-tool-writes">Writes</Badge>
          )}
        </span>
        <span className="ai-state ai-trace-outcome">
          <AiStatusDot tone={outcome.tone} />
          {outcome.label}
        </span>
        {step.authority && (
          <span className="ai-trace-authority">{authorityLabel(step.authority)}</span>
        )}
        {step.duration_ms !== undefined && (
          <span className="ai-trace-took">{`${step.duration_ms} ms`}</span>
        )}
        {hasDetail && (
          <Button
            size="sm"
            variant="link"
            className="ai-trace-toggle"
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
          >
            {open ? 'Hide' : 'What it passed and got back'}
          </Button>
        )}
      </div>

      {step.error && <p className="ai-decision-error mb-0">{step.error}</p>}

      {open && (
        <div className="ai-trace-detail">
          <span className="ai-panel-label">Arguments</span>
          <pre className="ai-decision-json">
            {step.arguments === undefined ? 'None recorded' : asJson(step.arguments)}
          </pre>
          <span className="ai-panel-label">Came back</span>
          <pre className="ai-decision-json">
            {step.result === undefined ? 'Nothing recorded' : asJson(step.result)}
          </pre>
        </div>
      )}
    </li>
  );
}

function AgentDryRun({
  scope,
  scopeLabel,
  version,
  dirty = false,
  tools = [],
}) {
  const [picking, setPicking] = useState(false);
  const [email, setEmail] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [unbuilt, setUnbuilt] = useState(false);

  /**
   * Has the draft been touched since the trace below was produced?
   *
   * A VERSION NUMBER IS NOT ENOUGH TO TELL, which is the trap this closes.
   * Re-saving a draft UPDATES THE EXISTING ROW and keeps its version, so
   * "ran v7" and "the stored v7 now says something else" look identical: the
   * trace would sit there labelled v7, apparently describing the thing about
   * to be published, having actually been produced by the previous wording.
   *
   * The trace is NOT thrown away when that happens, because reading it while
   * adjusting the rule is the whole loop. It is marked instead, and the mark
   * survives the save — it is cleared only by a run, which is the only thing
   * that makes a trace current again.
   */
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (dirty) setStale(true);
  }, [dirty]);

  // No draft is not the same as a draft with unsaved edits, and the two want
  // different sentences — see the note under the button.
  const runnable = !!scope && !!version && !dirty && !!email && !running;

  const handleRun = async () => {
    setRunning(true);
    setError('');
    setUnbuilt(false);
    setResult(null);
    try {
      const run = await AiDryRunService.run({ scope, version, emailKey: email.key });
      setResult(run);
      setStale(false);
    } catch (err) {
      if (isNotDeployed(err)) setUnbuilt(true);
      else setError(dryRunErrorText(err));
    } finally {
      setRunning(false);
    }
  };

  const trace = result?.trace || [];

  return (
    <section className="ai-panel ai-dry-run">
      <div className="ai-panel-title">
        <i className="fa fa-flask me-2" aria-hidden="true" />
        Try it on a real email
      </div>

      <p className="ai-panel-note">
        {`Runs the SAVED draft${version ? ` (v${version})` : ''} against one email`
         + ' the office has already had, and shows every tool it chose to call.'
         + ' Nothing is sent, nothing is queued, nothing is published and no'
         + ' decision is recorded — this is the rehearsal.'}
      </p>

      <div className="ai-dry-run-pick">
        {email ? (
          <div className="ai-dry-run-email">
            <span className="ai-dry-run-email-from">{email.from || 'Unknown sender'}</span>
            <span className="ai-dry-run-email-subject">{email.subject || '(no subject)'}</span>
            {email.date && (
              <span className="ai-dry-run-email-when">{formatDateTime(email.date) || email.date}</span>
            )}
          </div>
        ) : (
          <p className="mb-0">No email picked yet.</p>
        )}
        <Button
          variant="outline-secondary"
          onClick={() => setPicking(true)}
        >
          <i className="fa fa-inbox me-2" aria-hidden="true" />
          {email ? 'Pick a different email' : 'Pick an email'}
        </Button>
      </div>

      <div className="ai-dry-run-actions">
        <Button variant="primary" onClick={handleRun} disabled={!runnable}>
          {running ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Running…
            </>
          ) : (
            <>
              <i className="fa fa-play me-2" aria-hidden="true" />
              Run the draft against this email
            </>
          )}
        </Button>
      </div>

      <p className="ai-panel-note mb-0">
        {!version && 'Nothing to run — save a draft first.'}
        {version && dirty
          && 'Unsaved changes. A dry run tests the SAVED version, not what is in'
            + ' the box — save first, so the version you test is the version you'
            + ' publish.'}
        {version && !dirty && !email && 'Pick an email to run it against.'}
        {version && !dirty && !!email
          && `This runs v${version} exactly as it is stored, including its mode and`
            + ' tool authorities.'}
      </p>

      {error && (
        <p className="ai-decision-error">{error}</p>
      )}

      {unbuilt && (
        <div className="ai-panel ai-panel--warn ai-dry-run-unbuilt">
          <div className="ai-panel-title">
            <i className="fa fa-screwdriver-wrench me-2" aria-hidden="true" />
            The dry-run endpoint is not deployed yet
          </div>
          <p className="mb-0">
            {'This half is still being built alongside the drafting agent. The'
             + ' screen is asking for '}
            <code>{`POST ${DRY_RUN_PATH}`}</code>
            {' and getting nothing — so there is nothing wrong with your'
             + ' instruction, and saving and publishing are unaffected.'}
          </p>
        </div>
      )}

      {result && (
        <div className="ai-trace">
          {/* Re-saving a draft keeps its version, so the pill below cannot be
              trusted on its own to say whether this trace still describes
              what is stored. */}
          {stale && (
            <p className="ai-trace-stale">
              <i className="fa fa-clock-rotate-left me-2" aria-hidden="true" />
              {'The draft has changed since this run. The trace below is what'
               + ' the PREVIOUS wording did — run it again to see what the'
               + ' current one does.'}
            </p>
          )}
          <div className="ai-trace-summary">
            <span className="ai-trace-pill">
              {`v${or(result.version)} · ${scopeLabel || scope}`}
            </span>
            <span>{`Model: ${result.model || '—'}`}</span>
            <span>
              {result.duration_ms === undefined ? 'Took: —' : `Took: ${result.duration_ms} ms`}
            </span>
            <span>
              {result.tokens
                ? `Tokens: ${or(result.tokens.input)} in · ${or(result.tokens.output)} out`
                : 'Tokens: —'}
              {result.cost_usd ? ` · $${result.cost_usd}` : ''}
            </span>
          </div>

          {/* Read off the response, not asserted by us. A run that came back
              claiming it executed something is the one thing about this panel
              worth shouting about. */}
          {result.executed ? (
            <p className="ai-decision-error">
              {'The server did NOT confirm this was a dry run: it answered'
               + ' without `executed: false`. Treat anything below as something'
               + ' that may really have happened, and tell whoever owns the'
               + ' endpoint.'}
            </p>
          ) : (
            <p className="ai-panel-note">
              Confirmed by the server: nothing was executed, sent or recorded.
            </p>
          )}

          <span className="ai-panel-label">What it called</span>
          {trace.length === 0 ? (
            <p className="mb-0">
              {'It called no tools at all. That is a real answer — and if the'
               + ' rule you are testing was supposed to make it look something'
               + ' up, this is the run that says it did not.'}
            </p>
          ) : (
            <ol className="ai-trace-steps">
              {trace.map((step, index) => (
                <TraceStep
                  /* The position IS the identity here, and the usual
                     objection to an index key does not apply: a trace is an
                     ordered record of calls in which the same tool
                     legitimately appears twice with different arguments, and
                     it is replaced WHOLESALE by the next run — never
                     reordered, never spliced, never appended to. A key built
                     from the tool name alone would collide on the second call
                     to the same tool and collapse two steps into one, which
                     is a trace telling a lie about what the agent did. */
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${index}-${step.tool}`}
                  step={step}
                  index={index}
                  tools={tools}
                />
              ))}
            </ol>
          )}

          <span className="ai-panel-label">What it produced</span>
          {result.output === undefined || result.output === null ? (
            <p className="mb-0">Nothing was produced.</p>
          ) : (
            <pre className="ai-decision-json">{asJson(result.output)}</pre>
          )}
        </div>
      )}

      <Modal show={picking} onHide={() => setPicking(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="h5 mb-0">Pick an email to test against</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Bootstrap renders a modal in a portal at the document body, so
              the `.ai-instructions`-scoped panel classes do not reach in
              here. Bootstrap's own utilities do. */}
          <p className="text-muted small">
            {'The office’s real inbox. Pick one whose right answer you already'
             + ' know — that is what makes a trace worth reading.'}
          </p>
          <EmailInboxPanel
            /* The HTML is ignored: the dry run hands the server the KEY and
               the server reads the email itself, so keeping a copy of the
               body in state would be dead weight — and rendering it would
               mean putting an untrusted email's markup on the page, which is
               the correspondence viewer's job, not this panel's. */
            onSelect={(html, picked) => {
              setEmail(picked || null);
              setResult(null);
              setUnbuilt(false);
              setPicking(false);
            }}
          />
        </Modal.Body>
      </Modal>
    </section>
  );
}

export default AgentDryRun;
